import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import React, { useMemo, useState } from 'react';
import { useHistory } from 'react-router';
import Layout from '../components/Layout';
import * as Routes from '../constants/Routes';
import TableContainer from '@material-ui/core/TableContainer';
import Paper from '@material-ui/core/Paper';
import Table from '@material-ui/core/Table';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import { TableCell, TableSortLabel, TableBody, Checkbox, makeStyles, TablePagination } from '@material-ui/core';
import TableLoading from '../components/TableLoading';
import NoResultsRow from '../components/NoResultsRow';
import {
  useDeleteDoorModelsMutation,
  Pack,
  usePacksQuery,
  useDisablePacksMutation,
  useEnablePacksMutation,
} from '../queries';
import useTable from '../hooks/useTable';
import theme from '../theme';
import ErrorButton from '../components/ErrorButton';
import useDialog from '../hooks/useDialog';
import { useSnackbar } from 'notistack';

const useStyles = makeStyles({
  tableRow: {
    '&:hover': {
      cursor: 'pointer',
    },
  },
  activeStatus: {
    color: theme.palette.primary.main,
  },
  disabledStatus: {
    color: theme.palette.error.main,
  },
  stage: {
    width: '120px',
    fontSize: '10px',
    lineHeight: '24px',
  },
  readyStage: {
    backgroundColor: theme.palette.primary.main,
  },
  incompleteStage: {
    backgroundColor: theme.palette.error.main,
  },
  transferredStage: {
    backgroundColor: '#696A81',
  },
  selectedOption: {
    backgroundColor: 'rgba(255, 255, 255, 0.16) !important',
  },
  buttonContainer: {
    '& > *': {
      margin: theme.spacing(1),
    },
  },
});

export default function Packs() {
  const [selectedPacks, setSelectedPacks] = useState<Partial<Pack>[]>([]);
  const history = useHistory();
  const classes = useStyles();
  const { page, pageSize, sortField, sortDirection, pageChanged, pageSizeChanged, sort } = useTable('name');

  const { loading, error, data, refetch } = usePacksQuery({
    fetchPolicy: 'cache-and-network',
    variables: { skip: pageSize * page, take: pageSize, sortDirection, sortField },
  });
  const [deleteDoorModelsMutation] = useDeleteDoorModelsMutation();
  const { showDialog } = useDialog();
  const { enqueueSnackbar } = useSnackbar();

  const [disablePacksMutation] = useDisablePacksMutation();
  const [enablePacksMutation] = useEnablePacksMutation();

  const activeButton = useMemo(() => {
    const nbSelectedPacks = selectedPacks.length;
    const nbEnabledPacksSelected = selectedPacks.filter((p) => !p.deletedAt).length;
    const nbDisabledPacksSelected = selectedPacks.filter((p) => p.deletedAt).length;

    if (nbSelectedPacks > 0) {
      if (nbEnabledPacksSelected === 0) {
        return 'enable';
      } else if (nbDisabledPacksSelected === 0) {
        return 'disable';
      }
    } else {
      return 'create';
    }
  }, [selectedPacks]);

  const getPackStatus = (pack: Partial<Pack> | null | undefined) => {
    const status = pack?.deletedAt ? 'Disabled' : 'Active';
    const className = pack?.deletedAt ? classes.disabledStatus : classes.activeStatus;
    return (
      <Typography variant="body2" className={className}>
        {status}
      </Typography>
    );
  };

  const onDisablePacks = async () => {
    const validIds: number[] = [];
    selectedPacks.forEach((u) => {
      if (u && u.id) {
        validIds.push(parseInt(u.id));
      }
    });

    try {
      await disablePacksMutation({
        variables: {
          idListInput: {
            ids: validIds,
          },
        },
      });
      setSelectedPacks([]);
      refetch();
      const message =
        validIds.length > 1
          ? `The selected packs have been disabled successfully.`
          : `${selectedPacks[0].name} has been disabled successfully.`;
      enqueueSnackbar(message, { variant: 'success' });
    } catch (e) {
      enqueueSnackbar('An error has occurred executing the action.', { variant: 'error' });
    }
  };

  const onEnablePacks = async () => {
    const validIds: number[] = [];
    selectedPacks.forEach((u) => {
      if (u && u.id) {
        validIds.push(parseInt(u.id));
      }
    });

    try {
      await enablePacksMutation({
        variables: {
          idListInput: {
            ids: validIds,
          },
        },
      });
      setSelectedPacks([]);
      refetch();
      const message =
        validIds.length > 1
          ? `The selected pacs have been enabled successfully.`
          : `${selectedPacks[0].name} has been enabled successfully.`;
      enqueueSnackbar(message, { variant: 'success' });
    } catch (e) {
      enqueueSnackbar('An error has occurred executing the action.', { variant: 'error' });
    }
  };

  const onRowClick = (packId: string) => {
    history.push(`${Routes.Packs}/${packId}`);
  };

  const onAddPack = () => {
    history.push(`${Routes.PackAdd}`);
  };

  const isPackSelected = (pack: Partial<Pack> | null | undefined): boolean => {
    return selectedPacks.filter((d) => d?.id === pack?.id).length > 0;
  };

  const togglePack = (pack: Partial<Pack> | null | undefined, checked: boolean): void => {
    if (!pack) {
      return;
    }

    if (checked) {
      setSelectedPacks((prevState) => [...prevState, pack]);
    } else {
      setSelectedPacks((prevState) => prevState.filter((u) => u?.id !== pack?.id));
    }
  };

  return (
    <Layout>
      <Box display="flex" justifyContent="space-between">
        <Typography variant="h5">Packs</Typography>
        <Box display="flex" className={classes.buttonContainer}>
          {activeButton === 'enable' && (
            <Button variant="contained" color="secondary" onClick={onEnablePacks}>
              Enable pack
            </Button>
          )}
          {activeButton === 'disable' && (
            <ErrorButton variant="contained" onClick={onDisablePacks}>
              Disable pack
            </ErrorButton>
          )}
          {activeButton === 'create' && (
            <Button variant="contained" color="primary" onClick={onAddPack}>
              Add new pack
            </Button>
          )}
        </Box>
      </Box>
      <Box mt={3}>
        <TableContainer component={Paper}>
          <Table aria-label="Packs table">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" />
                <TableCell>
                  <TableSortLabel active={sortField === 'name'} direction={sortDirection} onClick={() => sort('name')}>
                    Name
                  </TableSortLabel>
                </TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && <TableLoading />}
              {!loading && !error && data?.packs.items.length === 0 && <NoResultsRow message="No packs to display" />}
              {!loading && error && <NoResultsRow message="There was an error fetching packs." />}
              {!loading &&
                !error &&
                data?.packs &&
                data?.packs.items.length > 0 &&
                data?.packs.items.map((pack, i) => (
                  <TableRow hover key={i} className={classes.tableRow}>
                    <TableCell>
                      <Checkbox
                        color="primary"
                        checked={isPackSelected(pack as Partial<Pack>)}
                        onChange={(e, checked) => togglePack(pack as Partial<Pack>, checked)}
                      />
                    </TableCell>
                    <TableCell onClick={() => onRowClick(pack.id)}>{pack.name}</TableCell>
                    <TableCell onClick={() => onRowClick(pack.id)}>{getPackStatus(pack as Partial<Pack>)}</TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={data?.packs.total ?? 0}
            rowsPerPage={pageSize}
            page={page}
            onChangePage={(_, newPage) => pageChanged(newPage)}
            onChangeRowsPerPage={pageSizeChanged}
          />
        </TableContainer>
      </Box>
    </Layout>
  );
}
